import { Add as AddIcon } from '@mui/icons-material';
import Neutral from '@mui/icons-material/Face';
import Dissatisfied from '@mui/icons-material/SentimentDissatisfied';
import Satisfied from '@mui/icons-material/SentimentSatisfied';
import { Box, Typography, Card, Button, Chip } from '@mui/material';
import IconButton from '@mui/material/IconButton';
import newObjectId from 'client/util/newObjectId';
import React, { useCallback, useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useOutletContext } from 'react-router';
import cartesian from '../../../../../util/cartesian';
import SlackRuleModal from './SlackRuleModal';
import messages from './messagesSlack';
import styles from '../IntegrationsListPage.css';
import { IoTrashOutline as DeleteIcon, IoPencil as EditIcon } from 'react-icons/io5';

const DEFAULT_RULES = [
  {
    _id: newObjectId(),
    surveys: null,
    condition: 'group:all,commented:true',
    channel: 'promoter-ninja',
    disabled: false,
  },
];

function IntegrationConfigSlack() {
  const { onIntegrationsPropertiesChange, integration, surveys, onReauthorize } =
    useOutletContext();
  const { formatMessage } = useIntl();
  const { rules = DEFAULT_RULES } = integration;

  const [modalOpen, setModalOpen] = React.useState(false);
  const [modalRuleIdx, setModalRuleIdx] = React.useState(null);

  const openEditModal = useCallback(idx => {
    setModalRuleIdx(idx);
    setModalOpen(true);
  }, []);

  const openAddModal = useCallback(() => {
    setModalRuleIdx(null);
    setModalOpen(true);
  }, []);

  const closeModal = useCallback(() => {
    setModalOpen(false);
    setModalRuleIdx(null);
  }, []);

  const handleModalSave = useCallback(
    (rule, idx) => {
      let newRules;
      if (typeof idx === 'number') {
        newRules = rules.map((r, i) => (i === idx ? rule : r));
      } else {
        // Ensure new rules have an _id
        const ruleWithId = { ...rule, _id: rule._id || newObjectId() };
        newRules = [...rules, ruleWithId];
      }
      onIntegrationsPropertiesChange(null, 'slack', { rules: newRules });
    },
    [rules, onIntegrationsPropertiesChange],
  );

  const icons = useMemo(
    () => ({
      Promoters: <Satisfied style={{ color: '#4fc44f' }} />,
      Passives: <Neutral style={{ color: '#f7c64b' }} />,
      Detractors: <Dissatisfied style={{ color: '#f66242' }} />,
    }),
    [],
  );

  const removeRule = useCallback(
    idx => () => {
      const newRules = rules.filter((_, i) => i !== idx);
      onIntegrationsPropertiesChange(null, 'slack', { rules: newRules });
    },
    [rules, onIntegrationsPropertiesChange],
  );

  const handleReauthorize = useCallback(() => {
    if (onReauthorize) {
      onReauthorize('slack');
    }
  }, [onReauthorize]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
        <FormattedMessage
          id="IntegrationsSettings.SlackSectionTitle"
          defaultMessage="Slack Integration"
        />
      </Typography>
      <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
        <FormattedMessage
          id="IntegrationsSettings.SlackSectionDescription"
          defaultMessage="Send Promoter Ninja scores and comments to a Slack channel. Please verify that the specified channel exists in Slack."
        />
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <Typography>
          <FormattedMessage
            id="IntegrationsSettings.SlackTeam"
            defaultMessage="Team name: {teamName}"
            values={{ teamName: integration.teamName }}
          />
        </Typography>
        <Button
          variant="text"
          onClick={handleReauthorize}
          color="muted"
          size="small"
          sx={{ ml: 1, textTransform: 'none' }}
        >
          {formatMessage(messages.reauthorize)}
        </Button>
      </Box>
      <Box className={styles.syncSurveysSection}>
        <Box className={styles.syncSurveysAddBtn}>
          <Button
            variant="text"
            color="primary"
            startIcon={<AddIcon />}
            onClick={openAddModal}
            className={styles.syncSurveysAddBtnInner}
            sx={{ fontWeight: 600 }}
          >
            {formatMessage(messages.addRule)}
          </Button>
        </Box>
        <Card variant="outlined" className={styles.syncSurveysTable}>
          <Box className={styles.syncSurveysHeader}>
            <Box className={styles.tableHeader}>{formatMessage(messages.slackChannel)}</Box>
            <Box className={styles.tableHeader}>{formatMessage(messages.surveys)}</Box>
            <Box className={styles.tableHeaderActions} />
          </Box>
          {rules.length === 0 ? (
            <Box className={styles.syncSurveysEmpty}>
              <FormattedMessage
                id="IntegrationsSettings.SlackNoRules"
                defaultMessage="No Slack rules configured."
              />
            </Box>
          ) : (
            rules.map((rule, idx) => (
              <Box
                key={rule._id}
                className={styles.syncSurveysRow}
                onClick={e => {
                  if (e.target.closest('button')) return;
                  openEditModal(idx);
                }}
                role="button"
                tabIndex={0}
                onKeyDown={e => {
                  if (e.key === 'Enter' || e.key === ' ') openEditModal(idx);
                }}
              >
                <Box className={styles.tableRow}>
                  <Typography color={rule.disabled ? 'text.disabled' : 'text.primary'}>
                    #{rule.channel.replace(/^#/, '')}
                  </Typography>
                  {rule.disabled && (
                    <Chip
                      label={formatMessage(messages.disabled)}
                      size="small"
                      variant="outlined"
                      color="default"
                      sx={{
                        height: 20,
                        fontSize: '0.7rem',
                      }}
                    />
                  )}
                </Box>
                <Box className={styles.tableRow}>
                  <Typography color={rule.disabled ? 'text.disabled' : 'text.primary'}>
                    {rule.surveys && rule.surveys.length > 0
                      ? rule.surveys
                          .map(id => (surveys.find(s => s.id === id) || {}).name)
                          .filter(Boolean)
                          .join(', ')
                      : formatMessage(messages.all)}
                  </Typography>
                </Box>
                <Box className={styles.tableRowActions}>
                  <IconButton
                    onClick={e => {
                      e.stopPropagation();
                      openEditModal(idx);
                    }}
                    color="primary"
                    size="small"
                    title={formatMessage(messages.edit)}
                  >
                    <EditIcon />
                  </IconButton>
                  <IconButton
                    onClick={e => {
                      e.stopPropagation();
                      removeRule(idx)();
                    }}
                    color="error"
                    size="small"
                    title={formatMessage(messages.remove)}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Box>
              </Box>
            ))
          )}
        </Card>
      </Box>
      <SlackRuleModal
        open={modalOpen}
        onClose={closeModal}
        ruleIdx={modalRuleIdx}
        onSave={handleModalSave}
        rules={rules}
        surveys={surveys}
        messages={messages}
        cartesian={cartesian}
        icons={icons}
      />
    </Box>
  );
}

export default IntegrationConfigSlack;
